import React, {useCallback, useEffect, useState} from 'react';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {useUserInfo} from '@/shared/hooks/useUserInfo';
import {slotPolicyApi} from '@/features/policy/slotManagement/api/SlotPolicyApi';
import '../styles/SlotManagement.css';
import {SaveSlotPolicyRequest, SlotPolicyDto} from '@/features/policy/slotManagement/types/slotPolicy.types';
import SlotPolicyModal from "@/features/policy/slotManagement/components/SlotPolicyModal";
import ConfirmModal from "@/shared/components/feedback/ConfirmModal";
import Pagination from "@/shared/components/dataDisplay/Pagination";

const PAGE_SIZE = 15;

const SlotManagementPage: React.FC = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [policies, setPolicies] = useState<SlotPolicyDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<SlotPolicyDto | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<SlotPolicyDto | null>(null);

    // ===== 데이터 조회 =====

    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await slotPolicyApi.getSlotPolicies();
            setPolicies(res.policies ?? []);
            setCurrentPage(1);
        } catch {
            showToast('슬롯 정책 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    // ===== 등록/수정 =====

    const handleOpenAdd = () => {
        setEditTarget(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (policy: SlotPolicyDto) => {
        setEditTarget(policy);
        setModalOpen(true);
    };

    const handleSave = async (req: SaveSlotPolicyRequest) => {
        const userInfo = getUserInfo();
        if (!userInfo) return;

        if (!req.standardAreas?.length) {
            const existing = policies.find(p => isGlobalPolicy(p));
            if (existing && existing.seqNo !== req.seqNo) {
                showToast('전체 협력사 적용(기본) 정책은 하나만 등록할 수 있습니다.', { type: ToastType.ERROR });
                return;
            }
        }

        try {
            await slotPolicyApi.saveSlotPolicy({ ...req, userId: userInfo.userId });
            showToast(req.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            setModalOpen(false);
            fetchPolicies();
        } catch (error: any) {
            const details = error?.originalError?.response?.data?.details;
            const detailMessages = details
                ? Object.values(details as Record<string, string>).join(', ')
                : '';
            const message = detailMessages || error?.message || '저장에 실패했습니다.';
            showToast(message, { type: ToastType.ERROR });
        }
    };

    // ===== 삭제 =====

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await slotPolicyApi.deleteSlotPolicy(deleteTarget.seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            setDeleteTarget(null);
            fetchPolicies();
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    // ===== 렌더 헬퍼 =====

    const formatDate = (d: string | null | undefined) => {
        if (!d || d.length !== 8) return '즉시 적용';
        return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
    };

    const isGlobalPolicy = (p: SlotPolicyDto) => !(p.standardAreas?.length);
    const hasGlobalPolicy = policies.some(isGlobalPolicy);

    const filteredPolicies = (nameFilter.trim()
            ? policies.filter(p => p.policyName.includes(nameFilter.trim()))
            : [...policies]
    ).sort((a, b) => {
        const ag = isGlobalPolicy(a) ? 0 : 1;
        const bg = isGlobalPolicy(b) ? 0 : 1;
        return ag - bg;
    });
    const totalPages = Math.ceil(filteredPolicies.length / PAGE_SIZE);
    const pagedPolicies = filteredPolicies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div style={{ padding: '24px' }}>
            {/* 헤더 */}
            <div className="slot-page-header">
                <h1 className="slot-page-title">슬롯 관리</h1>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="text"
                        value={nameFilter}
                        onChange={(e) => {
                            setNameFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="그룹명 검색"
                        style={{
                            width: '160px',
                            padding: '9px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                        }}
                    />
                    <button className="slot-add-btn" onClick={handleOpenAdd}>
                        + 정책 추가
                    </button>
                </div>
            </div>

            {/* 테이블 */}
            <div className="slot-table-wrap">
                {loading ? (
                    <div className="slot-empty">불러오는 중...</div>
                ) : filteredPolicies.length === 0 ? (
                    <div className="slot-empty">
                        {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 슬롯 정책이 없습니다.'}
                    </div>
                ) : (
                    <table className="slot-table">
                        <thead>
                        <tr>
                            <th>그룹명</th>
                            <th>표준권역</th>
                            <th>적용 시작일</th>
                            <th>필수 슬롯 수</th>
                            <th>수락률</th>
                            <th>슬롯 수</th>
                            <th>사용 여부</th>
                            <th>관리</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagedPolicies.map((policy) => {
                            const global = isGlobalPolicy(policy);
                            return (
                                <tr key={policy.seqNo} className={global ? 'row-global' : undefined}>
                                    <td className="text-center">
                                        {policy.policyName}
                                        {global && <span className="badge-global">기본</span>}
                                    </td>
                                    <td className="text-center">
                                        {global
                                            ? <span className="badge-global-area">모든 협력사 적용</span>
                                            : (policy.standardAreas?.join(', ') || '-')}
                                    </td>
                                    <td>{formatDate(policy.applyStartDate)}</td>
                                    <td>{policy.requiredSlotCnt ?? '-'}</td>
                                    <td>{policy.acceptanceRate != null ? `${policy.acceptanceRate}%` : '-'}</td>
                                    <td>{policy.slots?.length ?? 0}개</td>
                                    <td>
                                            <span className={policy.useYn === 'Y' ? 'badge-use-y' : 'badge-use-n'}>
                                                {policy.useYn}
                                            </span>
                                    </td>
                                    <td>
                                        <button className="btn-edit" onClick={() => handleOpenEdit(policy)}>
                                            수정
                                        </button>
                                        <button className="btn-delete" onClick={() => setDeleteTarget(policy)}>
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* 등록/수정 모달 */}
            <SlotPolicyModal
                isOpen={modalOpen}
                editTarget={editTarget}
                hasGlobalPolicy={hasGlobalPolicy}
                onSave={handleSave}
                onClose={() => setModalOpen(false)}
            />

            {/* 삭제 확인 모달 */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                title="슬롯 정책 삭제"
                message={`'${deleteTarget?.policyName}' 정책을 삭제하시겠습니까?`}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default SlotManagementPage;
